import React, {
  useState,
  useEffect,
} from "react";
import "./SystemBackup.css";
import LayoutContent from "../../LayoutContent";
import Header from "../../Header/Header";
import SettingsNavSubMenu from "../SettingsNavSubMenu";
import HeaderDropDownButton from "../../Header/HeaderDropDownButton";
import MtiButton from "../../Buttons/MtiButton";
import IconMoreAction from "../../../Icons/TopCommandPanel/MoreAction";
import SvgBackupDownload from "../../../Icons/TopCommandPanel/BackupDownload";
import SvgBackupDelete from "../../../Icons/TopCommandPanel/BackupDelete";
import SvgBackupConfig from "../../../Icons/TopCommandPanel/BackupConfig";
import SystemBackupApi from "../../../api/SystemBackupApi";
import Breadcrumb from "../../Header/Breadcrumb";
import {
  AutomaticBackupSetting,
  ConfigAndPreferenceBackupEntry,
} from "../../../Model/Backup";
import { Column, Filters, Row, CellProps } from "react-table";
import CommonTable from "../../Common/CommonTable";
import BackupNowPopUp from "./BackupNowModal";
import { ENotificationType } from "../../../Model/ENotificationType";
import AutomaticBackupSettingModal from "./AutomaticBackupSettingModal";
import {
  removeNotification,
  sendNotification,
} from "../../../redux/actions/NotificationAction";
import {
  removeModalNotification,
  sendModalNotification,
} from "../../../redux/actions/ModalNotificationAction";
import { useDispatch } from "react-redux";
import DateUtil from "../../../Utility/DateUtil";
import DateFilter from "../../Scheduling/DateFilter";

function getTableColumns(
  restoreButton: (row: Row<ConfigAndPreferenceBackupEntry>) => void,
  dateRange: { minDate: Date; maxDate: Date },
  setDateRange: (dateRange: { minDate: Date; maxDate: Date }) => void
): Column<ConfigAndPreferenceBackupEntry>[] {
  const RestoreButton = (props: CellProps<ConfigAndPreferenceBackupEntry>) => {
    return (
      <MtiButton
        size="small"
        variant="secondary"
        colorVariant="black"
        onClick={() => restoreButton(props.row)}
        style={{
          margin: "0px auto",
        }}
      >
        RESTORE
      </MtiButton>
    );
  };

  const name: Column<ConfigAndPreferenceBackupEntry> = {
    Header: "Notes",
    id: "name",
    accessor: (row) => {
      return row.name;
    },
  };
  const softwareVersion: Column<ConfigAndPreferenceBackupEntry> = {
    Header: "Software Version",
    id: "softwareversion",
    accessor: (row) => {
      return row.softwareVersion;
    },
    width: "10%",
  };
  const date: Column<ConfigAndPreferenceBackupEntry> = {
    Header: "Date",
    Filter: <DateFilter dateRange={dateRange} setDateRange={setDateRange} />,
    id: "date",
    accessor: (row) => {
      return DateUtil.toUsFormatString(new Date(row.date));
    },
    width: "20%",
  };

  const restore: Column<ConfigAndPreferenceBackupEntry> = {
    Header: <div>Action</div>,
    id: "restore",
    accessor: (row) => {
      return row.id;
    },
    Cell: RestoreButton,
    Filter: <div></div>,
    width: "10%",
  };

  return [date, softwareVersion, name, restore];
}

function getBackupRowStyle(
  value: ConfigAndPreferenceBackupEntry,
  isSelected: boolean
): React.CSSProperties {
  let style: React.CSSProperties = {};
  if (isSelected) {
    style.background = "#F7A246";
    style.color = "white";
    return style;
  }
  style.color = "black";
  return style;
}
function GetIdsBackupEntry(
  listEntry: ConfigAndPreferenceBackupEntry[]
): number[] {
  const ids: number[] = [];
  listEntry.forEach((element) => {
    ids.push(element.id);
  });
  return ids;
}
const initConfiguration: AutomaticBackupSetting = {
  backupIntervalInDays: 1,
  emailBackupNotification: false,
  lastBackupDate: new Date(Date.now()),
};

const CONFIGURATION = "CONFIGURATION";
const DELETE = "DELETE";
const DOWNLOAD = "DOWNLOAD";

export default function SystemBackup() {
  const dispatch = useDispatch();
  const [backupConfigs, setBackupConfigs] = useState(
    [] as ConfigAndPreferenceBackupEntry[]
  );
  const [selectedBackupConfigs, setSelectedBackupConfigs] = useState(
    [] as ConfigAndPreferenceBackupEntry[]
  );
  const [dateRange, setDateRange] = useState({
    minDate: new Date(2020, 1),
    maxDate: new Date(),
  });
  const [showbackupConfigModal, setShowbackupConfigModal] = useState(false);
  const [showAutomaticBackupConfigModal, setShowAutomaticBackupConfigModal] =
    useState(false);
  const [disableMoreActionButton, setDisableMoreActionButton] = useState(false);
  const [automaticBackupSetting, setAutomaticBackupSetting] =
    useState(initConfiguration);
  const [toggleSelectAllRow, setToggleSelectAllRow] = useState(
    undefined as boolean | undefined
  );
  const [clearFilter, setClearFilter] = useState({
    filters: [] as Filters<ConfigAndPreferenceBackupEntry>,
    action: () => {},
  });

  useEffect(() => {
    onUpdateBackupList(dateRange);
  }, [dateRange]);

  //enable or disable button
  useEffect(() => {
    if (selectedBackupConfigs.length === 0) setDisableMoreActionButton(true);
    else setDisableMoreActionButton(false);
  }, [selectedBackupConfigs, clearFilter]);

  const restoreButton = (row: Row<ConfigAndPreferenceBackupEntry>): void => {
    let selectedBackup = row.values;
    if (selectedBackup == undefined) {
      dispatch(
        sendNotification(
          ENotificationType.Error,
          `Selected id ${+row.id} is not found in list`
        )
      );
      return;
    }
    const backupName =
      row.values.name === null ? row.values.date : row.values.name;
    const backupId = selectedBackup.restore;
    dispatch(
      sendModalNotification(
        ENotificationType.Warning,
        "Restore Configuration",
        "Restore Configuration?",
        `Your current configuration will revert to the ${backupName} version`,
        () => {
          doRestore(backupId);
          dispatch(removeModalNotification());
        },
        () => {
          dispatch(removeModalNotification());
        }
      )
    );
  };

  const doRestore = (restoreBackupId: number) => {
    let id = restoreBackupId;
    onRestore(id);
    dispatch(removeModalNotification());
  };

  const columns = React.useMemo(
    () => getTableColumns(restoreButton, dateRange, setDateRange),
    [dateRange]
  );

  const onBackupNow = (name?: string) => {
    SystemBackupApi.backup(name)
      .then(() => {
        onUpdateBackupList(dateRange);
        dispatch(
          sendNotification(
            ENotificationType.Success,
            "Your system config has been backed up"
          )
        );
      })
      .catch((error) => {
        dispatch(sendNotification(ENotificationType.Error, error));
      });
  };

  const onDeleteBackup = () => {
    SystemBackupApi.delete(GetIdsBackupEntry(selectedBackupConfigs))
      .then(() => {
        onUpdateBackupList(dateRange);
      })
      .catch((error) => {
        dispatch(sendNotification(ENotificationType.Error, error));
      });
  };

  const onDownloadBackup = () => {
    SystemBackupApi.download(GetIdsBackupEntry(selectedBackupConfigs));
  };

  const onUpdateBackupList = ({
    minDate,
    maxDate,
  }: {
    minDate: Date;
    maxDate: Date;
  }) => {
    SystemBackupApi.getBackupList(minDate, maxDate)
      .then((configsAndPreference) => {
        setBackupConfigs(configsAndPreference);
      })
      .catch((error) => {
        dispatch(sendNotification(ENotificationType.Error, error));
      });
  };

  const onSetAutomaticBackupSetting = (
    automaticBackupSetting: AutomaticBackupSetting
  ) => {
    SystemBackupApi.setSetting(automaticBackupSetting)
      .then(() => {
        dispatch(
          sendModalNotification(
            ENotificationType.Success,
            "Restore Configuration",
            "Changes have been saved",
            "Please restart your system to apply changes.",
            () => {
              dispatch(removeModalNotification());
            }
          )
        );
      })
      .catch((error) => {
        dispatch(sendNotification(ENotificationType.Error, error));
      });
  };

  const onGetAutomaticBackupSetting = () => {
    SystemBackupApi.getSetting()
      .then((automaticBackupSetting) => {
        setAutomaticBackupSetting({
          ...automaticBackupSetting,
          lastBackupDate: new Date(automaticBackupSetting.lastBackupDate),
        });
        setShowAutomaticBackupConfigModal(true);
      })
      .catch((error) => {
        dispatch(sendNotification(ENotificationType.Error, error));
      });
  };

  const onRestore = (id: number) => {
    SystemBackupApi.restore(id)
      .then(() => {
        dispatch(
          sendModalNotification(
            ENotificationType.Success,
            "Restore Configuration",
            "Configuration restored",
            "Please restart your system to apply changes.",
            () => {
              dispatch(removeModalNotification());
            }
          )
        );
      })
      .catch((error) => {
        dispatch(removeNotification());

        let suggestion = "";
        if (error == "" || error == undefined || error == null)
          suggestion = "Contact support@formulatrix.com for assistance";
        else suggestion = error;
        dispatch(
          sendModalNotification(
            ENotificationType.Error,
            "Restore Configuration",
            "Failed to restore configuration",
            suggestion,
            () => {
              dispatch(removeModalNotification());
            }
          )
        );
      });
  };

  const doBackupAction = (name: string) => {
    onBackupNow(name);
  };

  const doSaveAutoamticbackupConfig = (
    automaticBackupSetting: AutomaticBackupSetting
  ) => {
    onSetAutomaticBackupSetting(automaticBackupSetting);
  };

  const doDeleteAction = () => {
    onDeleteBackup();
    removeModalNotification();
  };

  const moreActionOptions = [
    {
      value: CONFIGURATION,
      display: "Automatic backup Configuration",
      icon: <SvgBackupConfig />,
      disabled: false,
    },
    {
      value: DELETE,
      display: "Delete Backup",
      icon: <SvgBackupDelete />,
      disabled: disableMoreActionButton,
    },
    {
      value: DOWNLOAD,
      display: "Download Backup",
      icon: <SvgBackupDownload />,
      disabled: disableMoreActionButton,
    },
  ];
  const moreActionsClick = (id: string) => {
    switch (id) {
      case CONFIGURATION:
        onGetAutomaticBackupSetting();
        break;
      case DELETE:
        dispatch(
          sendModalNotification(
            ENotificationType.Warning,
            "Delete Configuration Backup",
            "Delete Configuration Backup?",
            "System will remove the configuration backup from database.",
            () => {
              doDeleteAction();
              dispatch(removeModalNotification());
            },
            () => {
              dispatch(removeModalNotification());
            }
          )
        );
        break;
      case DOWNLOAD:
        onDownloadBackup();
        break;
      default:
        break;
    }
  };

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: "System Backup",
                to: "/setting/system-backup",
                disabled: false,
              },
            ]}
          />
          <Header.RightSide>
            <MtiButton
              size="large"
              variant="primary"
              style={{
                margin: "0px 20px 0px 0px",
                padding: "0px 10px",
              }}
              onClick={async () => {
                setShowbackupConfigModal(true);
              }}
            >
              Backup Now
            </MtiButton>
            <HeaderDropDownButton
              id={"MORE_ACTIONS"}
              label={"More Actions"}
              icon={<IconMoreAction />}
              onChange={moreActionsClick}
              iconDisplayValues={moreActionOptions}
              dropDownStyle={{ left: -100 }}
            />
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.SubMenu>
        <SettingsNavSubMenu />
      </LayoutContent.SubMenu>
      <LayoutContent.Content>
        <CommonTable<ConfigAndPreferenceBackupEntry>
          setClearFilter={setClearFilter}
          setSelectedRows={setSelectedBackupConfigs}
          data={backupConfigs}
          columns={columns}
          getRowStyle={getBackupRowStyle}
          toggleSelectAllRow={toggleSelectAllRow}
          setToggleSelectAllRow={setToggleSelectAllRow}
          hidePaginationIfNotNeeded={true}
          pageLineSize={12}
        />
      </LayoutContent.Content>
      <BackupNowPopUp
        showPopUp={showbackupConfigModal}
        setShowPopUp={setShowbackupConfigModal}
        onBackupAction={doBackupAction}
      />
      <AutomaticBackupSettingModal
        automaticbackupSetting={automaticBackupSetting}
        onSetAutomaticBackupSetting={setAutomaticBackupSetting}
        showPopUp={showAutomaticBackupConfigModal}
        setShowPopUp={setShowAutomaticBackupConfigModal}
        onSaveAction={doSaveAutoamticbackupConfig}
      />
    </LayoutContent>
  );
}
